import { BaseComponent } from '../../base-component';
import { OcSwitchV2Events, HTMLOcSwitchV2Element } from '@otto-ec/otto-components/switch';
import { createProductComparison } from '../../product-comparison-api/client/product-comparison';
import { cookie } from '@otto-ec/global-resources/cookie';
import type { HTMLOcCheckboxV1Element, OcCheckboxV1Events } from '@otto-ec/otto-components/checkbox';
import { waitForMount } from '@otto-ec/otto-components';
import { trackEvent, trackView } from '@otto-ec/nexus-registry/pdp/tracking';
import { DataContainer } from '@otto-ec/tracking-bct';

export class ProductComparisonStarterComponent extends BaseComponent {
    private PRODUCT_COMPARISON_ENABLED_COOKIE = 'productComparisonEnabled';
    public rootSelector = '.js_pdp_product-comparison-starter';

    private productComparison = createProductComparison();

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }

        const { productBaseClass, variationId } = this.getRootElement().dataset;
        if (!productBaseClass || !variationId) {
            return;
        }

        const isProductComparisonEnabled = this.isProductComparisonEnabled();

        this.initializeSwitchElement(productBaseClass, isProductComparisonEnabled);
        await this.initializeCheckboxElement(productBaseClass, variationId);
        await this.initializeDiscoverMoreText(productBaseClass);
        await this.updateHeadline(productBaseClass);
        await this.updateStartButton(productBaseClass);

        await this.performViewTracking(isProductComparisonEnabled, productBaseClass, variationId);
    }

    private initializeSwitchElement(productBaseClass: string, checked: boolean): void {
        const switchElement = this.getChildElement<HTMLOcSwitchV2Element>('.js_pdp_product-comparison-starter__switch');

        let isEventTriggeredByInitialization = checked;

        switchElement.addEventListener('oc-property-change', async (e: OcSwitchV2Events['oc-property-change']) => {
            const isChecked = e.detail.checked?.current === true;

            switchElement.innerHTML = isChecked
                ? 'Produktvergleich aktiviert. Füge jetzt Produkte hinzu.'
                : 'Unentschlossen? Aktiviere den Produktvergleich.';

            this.toggleContentVisibility(isChecked);
            this.setProductComparisonEnabled(isChecked);

            if (isEventTriggeredByInitialization) {
                isEventTriggeredByInitialization = false;
            } else {
                if (isChecked) {
                    this.getChildElement<HTMLOcCheckboxV1Element>('.js_pdp_product-comparison-starter__checkbox').checked = true;
                }
                const productComparisonList = (await this.productComparison.getList(productBaseClass)) || [];
                trackEvent({
                    product_ProductComparisonActivity: [isChecked ? 'toggleOn' : 'toggleOff'],
                    product_ComparisonAmount: [String(productComparisonList.length)],
                });
            }
        });

        if (checked) {
            waitForMount(switchElement).then(element => {
                element.checked = true;
            });
        }
    }

    private async initializeCheckboxElement(productBaseClass: string, variationId: string) {
        let isEventTriggeredByInitialization = await this.productComparison.isOnList(productBaseClass, variationId);

        const checkboxElement = this.getChildElement<HTMLOcCheckboxV1Element>('.js_pdp_product-comparison-starter__checkbox');

        checkboxElement.addEventListener('oc-property-change', async (e: OcCheckboxV1Events['oc-property-change']) => {
            const isChecked = e.detail.checked?.current === true;
            if (isChecked) {
                if (!(await this.productComparison.isOnList(productBaseClass, variationId))) {
                    await this.productComparison.addToList(productBaseClass, variationId);
                }
                checkboxElement.textContent = 'Produkt hinzugefügt';
            } else {
                await this.productComparison.removeFromList(productBaseClass, variationId);
                checkboxElement.textContent = 'Produkt hinzufügen';
            }
            await this.updateHeadline(productBaseClass);
            await this.updateStartButton(productBaseClass);
            const productComparisonList = (await this.productComparison.getList(productBaseClass)) || [];
            if (isEventTriggeredByInitialization) {
                isEventTriggeredByInitialization = false;
            } else {
                trackEvent({
                    product_ProductComparisonActivity: [isChecked ? 'selected' : 'deselected'],
                    product_ComparisonAmount: [String(productComparisonList.length)],
                });
            }
        });

        if (await this.productComparison.isOnList(productBaseClass, variationId)) {
            checkboxElement.checked = true;
        }
    }

    private toggleContentVisibility(visible: boolean) {
        this.getChildElement('.js_pdp_product-comparison-starter__content').classList.toggle('oc-visually-hidden', !visible);
    }

    private async updateStartButton(productBaseClass: string) {
        const productComparisonList = (await this.productComparison.getList(productBaseClass)) || [];
        const startButtonElement = this.getChildElement<HTMLOcButtonV1Element>('.js_pdp_product-comparison-starter__start-button');
        startButtonElement.disabled = productComparisonList.length < 1;
        const linkForList = await this.productComparison.getLinkForList(productBaseClass);
        if (linkForList) {
            startButtonElement.href = linkForList;
        }
    }

    private async updateHeadline(productBaseClass: string) {
        const productComparisonList = (await this.productComparison.getList(productBaseClass)) || [];
        const productComparisonListLength = productComparisonList.length;
        const headlineElement = this.getChildElement('.js_pdp_product-comparison-starter__headline');
        headlineElement.textContent = `Produktvergleich (${productComparisonListLength} Produkt${productComparisonListLength === 1 ? '' : 'e'})`;
    }

    private async initializeDiscoverMoreText(productBaseClass: string) {
        const breadcrumbLinkElements = document.querySelectorAll<HTMLLinkElement>('.js_pdp_breadcrumb ul li a');

        const breadcrumbLinkElementsLength = breadcrumbLinkElements.length;
        if (breadcrumbLinkElementsLength < 2) {
            return;
        }

        const { href, textContent } = breadcrumbLinkElements[breadcrumbLinkElementsLength - 1];
        if (!href || !textContent) {
            return;
        }

        const discoverMoreLinkElement = document.createElement('oc-link-v2');
        discoverMoreLinkElement.base64Href = window.btoa(href);
        discoverMoreLinkElement.textContent = textContent.replace('alle ', '');
        discoverMoreLinkElement.addEventListener('click', async () => {
            const productComparisonList = (await this.productComparison.getList(productBaseClass)) || [];
            trackEvent({
                product_ComparisonLink: [discoverMoreLinkElement.textContent],
                product_ComparisonAmount: [String(productComparisonList.length)],
            });
        });

        const discoverMoreTextElement = this.getChildElement('.js_pdp_product-comparison-starter__discover-more-text');
        discoverMoreTextElement.append('Oder entdecke weitere ');
        discoverMoreTextElement.append(discoverMoreLinkElement);
        discoverMoreTextElement.append('.');

        discoverMoreTextElement.classList.remove('oc-visually-hidden');
    }

    private async performViewTracking(isProductComparisonEnabled: boolean, productBaseClass: string, variationId: string) {
        const productComparisonList = (await this.productComparison.getList(productBaseClass)) || [];
        const data: DataContainer = {
            product_ProductComparison: [isProductComparisonEnabled ? 'active' : 'view'],
            product_ComparisonAmount: [String(productComparisonList.length)],
        };
        if (productComparisonList.includes(variationId)) {
            data.product_ComparisonStatus = ['active'];
        }
        trackView(data);
    }

    public isProductComparisonEnabled(): boolean {
        const productComparisonEnabled = cookie.get(this.PRODUCT_COMPARISON_ENABLED_COOKIE);

        return !!productComparisonEnabled && productComparisonEnabled === 'true';
    }

    public setProductComparisonEnabled(enabled: boolean) {
        const productComparisonEnabled = cookie.get(this.PRODUCT_COMPARISON_ENABLED_COOKIE);

        if ((productComparisonEnabled === undefined || productComparisonEnabled === 'false') && enabled) {
            cookie.set(this.PRODUCT_COMPARISON_ENABLED_COOKIE, 'true');
        } else if (productComparisonEnabled === 'true' && !enabled) {
            cookie.remove(this.PRODUCT_COMPARISON_ENABLED_COOKIE);
        }
    }
}
